import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosGetToken from "./axiosGetToken";
import axiosDefault from "axios";
import { setAccessToken } from "../../services/tokenManager";
import { RootState } from "..";
import { clearAuthState } from "./authSlice";
import { readReferralCode, clearReferralCode } from "utils/referral";

interface Credentials {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  birthday?: string;
  address?: string;
  city?: string;
  zip?: string;
  country?: string;
  confirmPassword?: string;
}

export const verifyEmail = createAsyncThunk(
  "auth/verifyEmail",
  async (
    { verificationCode }: { verificationCode: string },
    { rejectWithValue },
  ) => {
    try {

      const response = await axiosGetToken.post<{
        success: boolean;
        message: string;
      }>("/users/verify-email", {
        verificationCode,
      });
      return response.data;
    } catch (error: unknown) {
      if (axiosDefault.isAxiosError(error) && error.response) {
        const errorMessage =
          error.response.data.message ||
          "Verification failed due to server error";
        return rejectWithValue(errorMessage);
      }
      return rejectWithValue(
        "An unexpected error occurred during email verification",
      );
    }
  },
);

export const authMe = createAsyncThunk(
  "auth/me",
  async (_, { rejectWithValue }) => {
    try {
      // Make request to /api/v1/auth/me endpoint to get user profile
      const response = await axiosGetToken.get<{
        user: any;
        identityRequireCheck: boolean;
        token?: string;
        accessToken?: string;
      }>("/v1/auth/me");

      if (response.data) {
        const { user, identityRequireCheck, token, accessToken } = response.data;
        return { user, identityRequireCheck, token, accessToken };
      }

      return rejectWithValue("User not authenticated");
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      return rejectWithValue("Failed to fetch user profile");
    }
  },
);

export const socialLogin = createAsyncThunk(
  "auth/socialLogin",
  async (
    {
      provider,
      redirectUrl,
    }: {
      provider:
        | "google"
        | "discord"
        | "facebook"
        | "apple"
        | "epic"
        | "steam"
        | "xbox"
        | "twitch"
        | "kick"
        | "x" // 'x' is for rebranded Twitter
        | "rockstar"
        | "nintendo";
      redirectUrl: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {

      const state = getState() as RootState;
      const selectedRegion = state.auth.selectedRegion;
      const referralCode = state.auth.pendingReferralCode || readReferralCode();

      const baseUrl = process.env.VITE_API_BASE_URL
        ? process.env.VITE_API_BASE_URL.replace(/\/$/, "")
        : "";
      const params = new URLSearchParams({
        redirectUrl,
        region: selectedRegion,
      });
      if (referralCode) {
        params.set("referralCode", referralCode);
      }
      const apiUrl = `${baseUrl}/api/v1/auth/${provider}?${params.toString()}`;

      window.location.href = apiUrl;
    } catch {
      return rejectWithValue("Error during social login");
    }
  },
);

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }: Credentials, { rejectWithValue }) => {
    try {
      const response = await axiosGetToken.post<{
        success: boolean;
        emailVerified: boolean;
        identityRequireCheck: boolean;
        token: string;
        accessToken?: string;
        user: any;
      }>("/v1/auth/login", { email, password });
      const effectiveToken = response.data.token || response.data.accessToken;
      if (effectiveToken) {
        setAccessToken(effectiveToken);
      }
      return response.data;
    } catch (error: unknown) {
      if (axiosDefault.isAxiosError(error) && error.response) {
        const errorMessage =
          error.response.data.error ||
          error.response.data.errors?.[0]?.msg ||
          "Login failed due to server error";
        return rejectWithValue(errorMessage);
      }
      return rejectWithValue("An unexpected error occurred during login");
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosGetToken.post("/v1/auth/logout");
      setAccessToken(null);
      dispatch(clearAuthState());
      return response.data;
    } catch (error: unknown) {
      if (axiosDefault.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Logout failed due to server error",
        );
      }
      return rejectWithValue("An unexpected error occurred during logout");
    }
  },
);

export const register = createAsyncThunk(
  "auth/register",
  async (
    {
      email,
      password,
      firstName,
      lastName,
      birthday,
      address,
      city,
      zip,
      country,
      confirmPassword,
    }: Credentials,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const pendingReferral = state.auth.pendingReferralCode;
      const referralCode = pendingReferral || readReferralCode();

      const response = await axiosDefault.post<{ user: any; token: string }>(
        "/api/v1/auth/register",
        {
          email,
          password,
          firstName,
          lastName,
          birthday,
          address,
          city,
          zip,
          country,
          confirmPassword,
          referralCode,
        },
      );

      if (referralCode) {
        clearReferralCode();
      }

      return response.data;
  } catch (error: unknown) {
      if (axiosDefault.isAxiosError(error) && error.response) {
        // Normalize 409 Conflict to a stable message so UI can react consistently
        if (error.response.status === 409) {
          return rejectWithValue("already exists");
        }
        const errorMessage =
          (error.response.data as any)?.error ||
          (error.response.data as any)?.errors?.[0]?.msg ||
          "Registration failed due to server error";
        return rejectWithValue(errorMessage);
      }
      return rejectWithValue(
        "An unexpected error occurred during registration",
      );
    }
  },
);
